import React from 'react';
import {
  Home,
  Radar,
  ListTodo,
  Users,
  FolderOpen,
  DollarSign,
  ClipboardList,
  FileText,
  BarChart2,
  Sparkles,
  Settings,
  UserCheck,
  Building,
  CalendarDays,
  Clock,
  FileWarning,
  Star,
  Receipt,
  ShoppingCart,
  PieChart,
  UserPlus,
  Store,
  BookOpen
} from 'lucide-react';
import { NAV_ITEMS, navItemForPath, type NavCatalogItem } from '@/lib/navigation-catalog';

export interface NavItem {
  /** the stable key of the navigation catalog (lib/navigation-catalog.ts): what the matrix switches ON / OFF per role */
  key: string;
  title: string;
  icon: React.ReactNode;
  href?: string;
  submenu?: NavItem[];
  /** The View permission (resource.view) that reveals this entry. A parent without one is shown while any of its children is. No permission = shown whenever its navigation key is. */
  permission?: string;
  /** 'project-setup': the entry opens the project-setup modal instead of navigating */
  action?: 'project-setup';
}

const icon = (Component: React.ComponentType<{ className?: string }>) => <Component className="h-5 w-5" />;

// the icon of each sidebar entry (the catalog is data only)
const ICONS: Record<string, React.ReactNode> = {
  home: icon(Home),
  command_center: icon(Radar),
  todos_mockup: icon(ListTodo),
  leads: icon(Users),
  'leads.lead_management': icon(UserCheck),
  'leads.proposals': icon(FileText),
  projects: icon(Building),
  project_management: icon(ClipboardList),
  'project_management.daily_logs': icon(FileText),
  'project_management.todos': icon(ClipboardList),
  'project_management.checklists': icon(ClipboardList),
  'project_management.material_orders': icon(ShoppingCart),
  'project_management.schedule': icon(CalendarDays),
  'project_management.change_orders': icon(FileWarning),
  'project_management.selections': icon(Star),
  'project_management.warranties': icon(FileText),
  'project_management.time_tracking': icon(Clock),
  'project_management.specifications': icon(FileText),
  drive: icon(FolderOpen),
  cost_management: icon(DollarSign),
  'cost_management.bids': icon(Receipt),
  'cost_management.bills': icon(Receipt),
  'cost_management.purchase_orders': icon(ShoppingCart),
  'cost_management.budgets': icon(DollarSign),
  'cost_management.invoices': icon(Receipt),
  'cost_management.catalog': icon(BookOpen),
  people: icon(Users),
  'people.team': icon(Users),
  'people.clients': icon(UserPlus),
  'people.vendors': icon(Store),
  business_insights: icon(PieChart),
  ai_chat: icon(Sparkles),
  settings: icon(Settings),
};

const toNavItem = (item: NavCatalogItem, submenu?: NavItem[]): NavItem => ({
  key: item.key,
  title: item.label,
  icon: ICONS[item.key],
  href: item.path,
  ...(submenu ? { submenu } : {}),
  ...(item.permission ? { permission: item.permission } : {}),
  ...(item.action ? { action: item.action } : {}),
});

// BUG-16 (2026-09-19): single source of truth for the nav tree, shared by the desktop Sidebar (sidebar-navigation.tsx) and the mobile drawer (mobile-navigation.tsx).
// Navigation visibility (2026-11): the tree is BUILT from the navigation catalog -- the sidebar entries are its menus and submenus; tabs and routes that are not entries
// of the sidebar are not listed here. Which entries a person sees is decided by the navigation matrix (filterNavigation's `isVisible`), not by this list.
export const navigationItems: NavItem[] = NAV_ITEMS
  .filter((item) => item.type === 'menu')
  .map((menu) => {
    const children = NAV_ITEMS.filter((child) => child.type === 'submenu' && child.parent === menu.key).map((child) => toNavItem(child));
    return toNavItem(menu, children.length > 0 ? children : undefined);
  });

/**
 * The navigation a person may see: an entry whose navigation key is OFF for the role is dropped (with everything under it), an entry whose View permission they lack
 * is dropped, and a parent is kept only while one of its children is (a parent with a page of its own and no children stays when its own permission is held).
 * `isVisible` is required on purpose: there is no "show everything" default.
 */
export function filterNavigation(items: NavItem[], can: (permission: string) => boolean, isVisible: (key: string) => boolean): NavItem[] {
  const result: NavItem[] = [];
  for (const item of items) {
    if (!isVisible(item.key)) continue;
    if (item.submenu) {
      const children = filterNavigation(item.submenu, can, isVisible);
      if (children.length === 0) continue;
      result.push({ ...item, submenu: children });
    } else if (!item.permission || can(item.permission)) {
      result.push(item);
    }
  }
  return result;
}

/**
 * The View permissions that open a path: the catalog element whose address is the longest match of the path decides -- its own permission, or (a parent page) any of
 * its children's. null = the path carries no permission (Home, Settings, KrewUp AI...); the navigation matrix still governs it (RoutePermissionGuard).
 */
export function permissionsForPath(pathname: string): string[] | null {
  const best = navItemForPath(pathname);
  if (!best) return null;
  if (best.permission) return [best.permission];
  const own = NAV_ITEMS.filter((child) => child.parent === best.key && child.type === 'submenu').map((child) => (child as NavCatalogItem).permission).filter((p): p is string => !!p);
  return own.length > 0 ? own : null;
}
